'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import type { ElasticBranch } from '@//app/api/search/branch/[id]';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Link } from '@//components/Link';
import { useLegacyTrackOutboundLinkGoogleMaps } from '@//data/tracking/useLegacyTrackOutboundLinkGoogleMaps';
import { useTriggerOnce } from '@//hooks/useTriggerOnce';
import { getGoogleMapsUrl } from '@//utils/maps';
import styles from './SellerSection.module.scss';

interface SellerAddressProps {
  branch: ElasticBranch;
  vehicle: ElasticVehicle;
}

const SellerAddress: React.FC<SellerAddressProps> = ({ branch, vehicle }) => {
  const t = useTranslations('pages.item.sellerSection');
  const tCommon = useTranslations('other.common');
  const { trackLegacyOutboundLinkGoogleMaps } = useLegacyTrackOutboundLinkGoogleMaps();

  const trackLegacyOutboundLinkGoogleMapsOnce = useTriggerOnce((v: ElasticVehicle) =>
    trackLegacyOutboundLinkGoogleMaps(v),
  );

  const onClick = useCallback(
    () => trackLegacyOutboundLinkGoogleMapsOnce(vehicle),
    [vehicle, trackLegacyOutboundLinkGoogleMapsOnce],
  );

  if (!branch.position) return null;
  return (
    <>
      <dt className={styles.asideListLabel}>{tCommon('address')}</dt>
      <dd className={styles.asideListValue}>
        <Link
          href={getGoogleMapsUrl({
            street: branch.position.street,
            zip: branch.position.zip,
            city: branch.position.city,
          })}
          title={t('googleMapsLinkTitle')}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={onClick}
          prefetch="none"
        >
          {branch.position.street}, {branch.position.city}
        </Link>
      </dd>
    </>
  );
};

export default SellerAddress;
